import {
  $getRoot,
  EditorState,
  LexicalEditor,
  SerializedEditorState,
} from "lexical";
import React, { useMemo } from "react";
import { Attachment } from "src/types/messages/content";
import {
  DEFAULT_SETTINGS,
  Settings,
  VariableMode,
} from "../context/SettingsContext/settings";
import AllNodes from "../nodes/AllNodes";
import { VariableNode } from "../nodes/VariableNode";
import { deepTransformToVariableNodes } from "../plugins/VariablesPlugin/utils";
import {
  editorStateToHtml,
  editorStateToPlainText,
  importHtml,
  importPlainText,
  isEmptyLexicalState,
  mergeSerializedEditorStates,
  parseSerializedEditorState,
  replaceInlineImagesWithCID,
  restoreCIDsInSerializedState,
  serializedEditorStateToHtml,
  serializedEditorStateToHtmlWithCIDs,
  serializedEditorStateToPlainText,
} from "../utils/converters";
import { createHeadlessEditor } from "../utils/headlessEditor";
import { useHTMLConfig } from "./useHTMLConfig";
import { useVariables } from "./useVariables";

type Options = {
  variableMode: VariableMode;
} & Partial<Settings>;

export function useContentConversion({ variableMode, ...settings }: Options) {
  const { isValid } = useVariables(settings);

  return useMemo(() => {
    const expandedMustache = variableMode === "expanded";

    return {
      // ── Live editor (delegate to converters + variable transform) ───────

      fromPlainText: (editor: LexicalEditor | undefined, text: string) => {
        if (!editor) return;
        importPlainText(editor, text, true);
        if (editor.hasNodes([VariableNode])) {
          editor.update(() => {
            $getRoot()
              .getChildren()
              .forEach((child) => deepTransformToVariableNodes(child, isValid));
          });
        }
      },

      toPlainText: (
        editor: LexicalEditor | undefined,
        editorState?: EditorState
      ) => {
        if (!editor) return "";
        const state = editorState ?? editor.getEditorState();
        return editorStateToPlainText(state, expandedMustache);
      },

      fromHtml: (editor: LexicalEditor | undefined, html: string) => {
        if (!editor) return;
        importHtml(editor, html);
        if (editor.hasNodes([VariableNode])) {
          editor.update(() => {
            $getRoot()
              .getChildren()
              .forEach((node) => deepTransformToVariableNodes(node, isValid));
          });
        }
      },

      toHtml: (
        editor: LexicalEditor | undefined,
        editorState?: EditorState
      ): string => {
        if (!editor) return "";
        return editorStateToHtml(editor, editorState);
      },

      toHtmlWithCIDs: (
        editor: LexicalEditor | undefined,
        editorState?: EditorState
      ) => {
        if (!editor) return { html: "", attachments: [] };
        const html = editorStateToHtml(editor, editorState);
        return replaceInlineImagesWithCID(html);
      },

      // ── Serialized (delegate to converters + variable transform where needed) ─

      serialized: {
        toPlainText: (
          editor: LexicalEditor,
          input: SerializedEditorState | undefined | null
        ) => serializedEditorStateToPlainText(editor, input, expandedMustache),

        toHtml: (
          editor: LexicalEditor,
          serializedState: SerializedEditorState | undefined | null
        ) => serializedEditorStateToHtml(editor, serializedState),

        toHtmlWithCIDs: (
          editor: LexicalEditor,
          serializedState: SerializedEditorState | undefined | null
        ) => serializedEditorStateToHtmlWithCIDs(editor, serializedState),

        fromHtml: (
          editor: LexicalEditor,
          html: string
        ): SerializedEditorState => {
          importHtml(editor, html, true);
          return editor.getEditorState().toJSON() as SerializedEditorState;
        },

        fromPlainText: (
          editor: LexicalEditor,
          text: string
        ): SerializedEditorState => {
          importPlainText(editor, text, true);
          if (editor.hasNodes([VariableNode])) {
            editor.update(
              () => {
                $getRoot()
                  .getChildren()
                  .forEach((child) =>
                    deepTransformToVariableNodes(child, isValid)
                  );
              },
              { discrete: true }
            );
          }
          return editor.getEditorState().toJSON() as SerializedEditorState;
        },
      },

      // ── Pure utilities (delegate to converters) ─────────────────────────

      isEmpty: (
        value: string | EditorState | SerializedEditorState | null | undefined
      ) => isEmptyLexicalState(value),

      parse: (value: string | SerializedEditorState | undefined | null) =>
        parseSerializedEditorState(value),

      merge: (
        baseState: SerializedEditorState | undefined | null,
        appendState: SerializedEditorState | undefined | null
      ) => mergeSerializedEditorStates(baseState, appendState),

      restoreCIDs: (
        serializedState: SerializedEditorState | undefined | null,
        attachments: Attachment[]
      ) => restoreCIDsInSerializedState(serializedState, attachments),
    };
  }, [isValid, variableMode]);
}

export function useHeadlessContentConversion({
  variableMode,
  ...partialSettings
}: Options) {
  const settings = React.useMemo(() => {
    return {
      ...DEFAULT_SETTINGS,
      ...partialSettings,
    };
  }, [partialSettings]);
  const htmlConfig = useHTMLConfig(settings);
  const converter = useContentConversion(settings);
  const editor = useMemo(
    () =>
      createHeadlessEditor({
        nodes: AllNodes,
        html: htmlConfig,
      }),
    [htmlConfig]
  );

  return useMemo(() => {
    return {
      toPlainText: (input: SerializedEditorState | undefined | null) =>
        converter.serialized.toPlainText(editor, input),

      toHtml: (serializedState: SerializedEditorState | undefined | null) =>
        converter.serialized.toHtml(editor, serializedState),

      toHtmlWithCIDs: (
        serializedState: SerializedEditorState | undefined | null
      ) => converter.serialized.toHtmlWithCIDs(editor, serializedState),

      fromHtml: (html: string): SerializedEditorState =>
        converter.serialized.fromHtml(editor, html),

      fromPlainText: (text: string): SerializedEditorState =>
        converter.serialized.fromPlainText(editor, text),

      isEmpty: converter.isEmpty,
      parse: converter.parse,
      merge: converter.merge,
      restoreCIDs: converter.restoreCIDs,
    };
  }, [converter, editor]);
}
